import React from 'react';
import { createBrowserRouter, RouteObject, RouterProvider } from 'react-router-dom';

import { rootRoutes } from '@/app/appRoutes';
import { DefaultPathRoute } from '@/app/providers/AppRouter/DefaultPathRoute';
import { PermissionRoute } from '@/app/providers/AppRouter/PermissionRoute';
import { RouteErrorElement } from '@/app/providers/AppRouter/RouteErrorElement';

import { RouteConfig } from '@/shared/lib/ts-router';

/**
 * Экран ошибки маршрутов страниц: они вложены в маршрут каркаса приложения, поэтому падение
 * страницы показывается в области содержимого, не снимая левое меню и верхнюю панель.
 * Назначается всем вложенным маршрутам по умолчанию — иначе ошибка дошла бы до маршрута
 * каркаса и забрала бы навигацию вместе с собой.
 */
const pageErrorElement = <RouteErrorElement isEmbedded />;

const transformRoutes = (routes: readonly RouteConfig[], isNested = false): RouteObject[] => {
  return routes.flatMap((route) => {
    const { path, element, errorElement, getDefaultPath, children, viewPermission } = route;

    let routeElement = element;
    const routeErrorElement = errorElement ?? (isNested ? pageErrorElement : undefined);

    if (viewPermission) {
      routeElement = <PermissionRoute viewPermission={viewPermission}>{routeElement}</PermissionRoute>;
    }

    if (getDefaultPath) {
      const redirectRoute = {
        index: true,
        element: <DefaultPathRoute getDefaultPath={getDefaultPath} />,
        errorElement: pageErrorElement,
      };

      return {
        path,
        element: routeElement,
        errorElement: routeErrorElement,
        children: [redirectRoute, ...(children ? transformRoutes(children, true) : [])],
      };
    }

    const routeChildren = children ? transformRoutes(children, true) : [];
    if (path && routeElement) {
      return {
        path,
        element: routeElement,
        errorElement: routeErrorElement,
        children: routeChildren,
      };
    } else {
      return routeChildren;
    }
  });
};

const router = createBrowserRouter(transformRoutes(rootRoutes));

export const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};
